<template>
    <!--App.vue-->
    <v-app class="global-font">
        <div v-if="showCustomSplash" style="
            position: fixed; inset: 0; background: #000; z-index: 99999;
            display: flex; flex-direction: column; align-items: center; justify-content: center;">
            <img src="/snapydreamtv.png" style="width: 240px; height: 240px;" />
            <div
                style="position: absolute; bottom: 60px; display: flex; flex-direction: column; align-items: center; gap: 16px;">
                <p class="text-wrap"
                    style="margin: 0; color: white; font-size: 16px; font-weight: 600; letter-spacing: 1px; font-family: 'Plus Jakarta Sans', sans-serif;">
                    {{ $t('app.tagline') }}
                </p>
                <v-progress-circular indeterminate color="white" size="32" width="2"></v-progress-circular>
            </div>
        </div>

        <router-view v-slot="{ Component }">
            <keep-alive :include="mainPages">
                <component :is="Component" />
            </keep-alive>
        </router-view>

        <DialogWarning :dialog="showDialog" @update:dialog="setShowDialog" />

        <v-snackbar v-model="showSnackbar" :timeout="10000" class="mb-16 net">
            {{ snackbarMessage }}

            <template v-slot:actions>
                <v-btn color="pink" variant="text" @click="reloadPage()">
                    {{ $t('app.reload') }}
                </v-btn>
            </template>
        </v-snackbar>
    </v-app>
</template>

<script>
import { onMounted } from 'vue';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import router from '@/router';
import { getAuth, onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import RevenueCatService from '@/services/revenuecat';
import { StatusBar } from '@capacitor/status-bar';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { mapState, mapMutations } from 'vuex';
import DialogWarning from './components/connected-components/Dialog-Warning.vue';
import { PrivacyScreen } from '@capacitor-community/privacy-screen';
import { db } from '@/main';
import { collection, getDocs, query, where, doc, setDoc } from 'firebase/firestore';
import { Device } from '@capacitor/device';
import { NavigationBar, Style } from '@capawesome/capacitor-navigation-bar';

export default {
    components: { DialogWarning },

    data() {
        return {
            isOnline: navigator.onLine,
            showSnackbar: false,
            snackbarMessage: '',
            showCustomSplash: false,
        };
    },

    computed: {
        ...mapState(['showDialog']),

        mainPages() {
            return [
                'homePage',
                'NotificationsPage',
                'ProfilPage',
                'ProfilPageOffline',
                'PublishPage',
                'unConnected',
                'LibraryPageOffline',
                'NotificationsPageOffline',
                'PublishPageOffline',
            ];
        },
    },

    methods: {
        setLanguageFromCountry(country) {
            if (localStorage.getItem('langManuallySet') === 'true') return;

            // 🔥 Pays francophones (Afrique + Europe + autres)
            const frCountries = [
                // Europe
                'FR', 'BE', 'LU', 'MC', 'CH',
                // Afrique du Nord (arabophones mais français très utilisé)
                'MA', 'DZ', 'TN',
                // Afrique de l'Ouest francophone
                'CI', 'SN', 'ML', 'BF', 'NE', 'TG', 'BJ', 'GN', 'GW',
                // Afrique Centrale francophone
                'CM', 'CD', 'CG', 'GA', 'CF', 'TD', 'GQ',
                // Afrique de l'Est francophone
                'DJ', 'RW', 'BI', 'MG', 'KM',
                // Îles francophones
                'MU', 'SC', 'RE', 'PM', 'MF', 'GP', 'MQ', 'GF',
                // Autres
                'MR', 'VU', 'PF', 'NC',
            ];

            // 🔥 Pays hispanophones
            const esCountries = [
                'ES', 'MX', 'CO', 'AR', 'PE', 'VE', 'CL', 'EC',
                'GT', 'CU', 'BO', 'DO', 'HN', 'PY', 'SV', 'NI',
                'CR', 'PA', 'UY', 'PR',
            ];

            // 🔥 Haïti uniquement
            const htCountries = ['HT'];

            // 🔥 Pays anglophones africains — tombent dans le défaut EN
            // NG, GH, KE, TZ, UG, ZA, ZM, ZW, MW, BW, NA, SZ, LS,
            // SL, LR, GM, ET, SS, SD, ER, SO, EG, LY + tous les autres

            let lang = 'en'; // 🔥 Défaut : anglais
            if (htCountries.includes(country)) lang = 'ht';
            else if (frCountries.includes(country)) lang = 'fr';
            else if (esCountries.includes(country)) lang = 'es';

            localStorage.setItem('lang', lang);
            this.$i18n.locale = lang;
        },

        ...mapMutations(['setShowDialog']),

        // 🔥 Messages traduits
        handleOnline() {
            this.isOnline = true;
            this.snackbarMessage = this.$t('app.online');
            this.showSnackbar = true;
        },
        handleOffline() {
            this.isOnline = false;
            this.snackbarMessage = this.$t('app.offline');
            this.showSnackbar = true;
        },

        async signInAnonymouslyIfNeeded() {
            try {
                const auth = getAuth();

                await new Promise((resolve) => {
                    const unsubscribe = onAuthStateChanged(auth, (user) => {
                        unsubscribe();
                        resolve(user);
                    });
                    setTimeout(() => { unsubscribe(); resolve(null); }, 25000);
                });

                if (auth.currentUser) return;
                if (localStorage.getItem('alreadyConnected') === 'true') return;

                let deviceId = null;
                if (Capacitor.isNativePlatform()) {
                    const info = await Device.getId();
                    deviceId = info.identifier;
                }

                if (deviceId) {
                    const q = query(
                        collection(db, 'users'),
                        where('deviceId', '==', deviceId),
                        where('isAnonymous', '==', true)
                    );
                    const snapshot = await getDocs(q);

                    if (!snapshot.empty) {
                        const oldData = snapshot.docs[0].data();
                        const result = await signInAnonymously(auth);
                        const newUid = result.user.uid;
                        const country = localStorage.getItem('userCountry');
                        await setDoc(doc(db, 'users', newUid), { ...oldData, userId: newUid, deviceId, country: country || oldData.country || null }, { merge: true });
                        return;
                    }
                }

                const result = await signInAnonymously(auth);
                const uid = result.user.uid;
                const country = localStorage.getItem('userCountry');

                await setDoc(doc(db, 'users', uid), {
                    userId: uid, isAnonymous: true,
                    deviceId: deviceId || null,
                    bookmarked: [], youRead: [],
                    gift: 0, creditsBought: 0,
                    premium: false,
                    userDisplayId: uid,
                    newUser: true,
                    createdAt: new Date(),
                       country: country || null, 
                }, { merge: true });

            } catch (e) {
                console.error('Erreur auth anonyme:', e);
            }
        },

        async configureStatusBar() {
            if (Capacitor.isNativePlatform()) {
                try {
                    await StatusBar.setOverlaysWebView({ overlay: true });
                    await StatusBar.setBackgroundColor({ color: '#000000' });
                    await StatusBar.setStyle({ style: 'DARK' });
                    await StatusBar.show();
                    await ScreenOrientation.lock({ orientation: 'portrait' });
                } catch (e) {
                    console.log('🟡 StatusBar non disponible:', e);
                }
            }
        },

        async configurateScreeProtection() {
            if (Capacitor.isNativePlatform()) {
                try {
                    await PrivacyScreen.disable();
                } catch (err) {
                    console.error("Error disabling privacy screen", err);
                }
            }
        },

        ensureUserDisplayId() {
            let userId = localStorage.getItem('userDisplayId');
            if (!userId) {
                userId = Math.floor(10000000 + Math.random() * 90000000).toString();
                localStorage.setItem('userDisplayId', userId);
            }
            return userId;
        },

        async checkFirstLaunch() {
            if (!Capacitor.isNativePlatform()) return;
            if (localStorage.getItem('hasLaunched_v2')) return;
            if (localStorage.getItem('alreadyConnected') === 'true') return;

            try {
                const q = query(collection(db, 'book-style-sms'), where('publish', '==', 'wi'));
                const snapshot = await getDocs(q);

                if (!snapshot.empty) {
                    // 🔥 Filtrer pour exclure le document SUYtHxnXOpbnqPxiZtUD
                    const availableDocs = snapshot.docs.filter(doc => doc.id !== 'SUYtHxnXOpbnqPxiZtUD');

                    if (availableDocs.length > 0) {
                        const randomIndex = Math.floor(Math.random() * availableDocs.length);
                        const bookId = availableDocs[randomIndex].id;
                        localStorage.setItem('hasLaunched_v2', 'true');
                        await this.$router.isReady();
                        this.$router.push({ name: 'book-sms', params: { id: bookId } });
                        return;
                    }
                }
            } catch (e) {
                console.error('Erreur first launch:', e);
            }

            localStorage.setItem('hasLaunched_v2', 'true');
        },

        // 🔥 Détection pays — MOBILE (Firestore + localStorage)
        async detectAndSaveCountry() {
            try {
                const lastDetected = localStorage.getItem('countryDetectedAt');
                const now = Date.now();
                if (lastDetected && now - parseInt(lastDetected) < 7 * 24 * 60 * 60 * 1000) return;

                const response = await fetch('https://cloudflare.com/cdn-cgi/trace');
                const text = await response.text();
                const country = text.split('\n').find(line => line.startsWith('loc='))?.split('=')[1]?.trim();

                if (country) {
                    localStorage.setItem('userCountry', country);
                    localStorage.setItem('countryDetectedAt', now.toString());
                    this.setLanguageFromCountry(country);

                    const auth = getAuth();
                    if (auth.currentUser?.uid) {
                        // User déjà connecté → sauvegarder directement
                        const userRef = doc(db, 'users', auth.currentUser.uid);
                        await setDoc(userRef, { country }, { merge: true });
                    }
                    // Sinon signInAnonymouslyIfNeeded() récupèrera userCountry depuis localStorage
                }
            } catch (e) {
                // silencieux
            }
        },
        // 🔥 Détection pays — WEB (localStorage + Firestore si connecté)
        async detectAndSaveCountryForWeb() {
            try {
                const lastDetected = localStorage.getItem('countryDetectedAt');
                const now = Date.now();
                if (lastDetected && now - parseInt(lastDetected) < 7 * 24 * 60 * 60 * 1000) return;

                const response = await fetch('https://cloudflare.com/cdn-cgi/trace');
                const text = await response.text();

                const country = text
                    .split('\n')
                    .find(line => line.startsWith('loc='))
                    ?.split('=')[1]
                    ?.trim();

                if (country) {
                    // 🔥 localStorage uniquement
                    localStorage.setItem('userCountry', country);
                    localStorage.setItem('countryDetectedAt', now.toString());
                    this.setLanguageFromCountry(country);
                }
            } catch (e) {
                // silencieux
            }
        },


         async configureNavigationBar() {
  if (Capacitor.getPlatform() === 'android') {
    try {
      // Rend la barre de navigation transparente
      await NavigationBar.setColor({ color: 'transparent' });
      // Optionnel : ajuste la couleur des icônes de la barre (LIGHT ou DARK)
      await NavigationBar.setStyle({ style: Style.Light });
    } catch (e) {
      console.log('NavigationBar non disponible :', e);
    }
  }
        },

        reloadPage() {
            // this.snackbar = false;
            window.location.reload()
        }

    },

    setup() {
        let pendingDeepLink = null;
        onMounted(async () => {
            CapacitorApp.addListener('appUrlOpen', (event) => {
                const url = new URL(event.url);
                const id = url.pathname.replace('/', '');
                if (id && localStorage.getItem('alreadyConnected') !== 'true') {
                    pendingDeepLink = id;
                    router.push({ name: 'book-sms', params: { id } });
                }
            });
        });

        return { getPendingDeepLink: () => pendingDeepLink };
    },

    async mounted() {
        if (Capacitor.isNativePlatform()) {

            // 🔥 1. LANGUE EN PREMIER — avant que les composants se montent
            await this.detectAndSaveCountry();

            // 🔥 2. Splash Android
            if (Capacitor.getPlatform() === 'android') {
                this.showCustomSplash = true;
                setTimeout(() => { this.showCustomSplash = false; }, 5000);
            }

            // 🔥 3. StatusBar
            try {
                const currentRoute = this.$router.currentRoute?.value?.name;
                if (currentRoute !== 'book-sms') {
                    await StatusBar.show();
                    await StatusBar.setOverlaysWebView({ overlay: true });
                    await StatusBar.setBackgroundColor({ color: '#000000' });
                    await StatusBar.setStyle({ style: 'DARK' });
                }

                setTimeout(async () => {
                    try {
                        // 🔥 Verifye ankò nan setTimeout — rout la ka chanje
                        const routeNow = this.$router.currentRoute?.value?.name;
                        if (routeNow !== 'book-sms') {
                            await StatusBar.setOverlaysWebView({ overlay: true });
                            await StatusBar.setBackgroundColor({ color: '#000000' });
                            await StatusBar.setStyle({ style: 'DARK' });
                        }
                        await this.configurateScreeProtection();
                    } catch (e) {
                        console.log('StatusBar error:', e);
                    }
                    requestAnimationFrame(() => {
                        document.body.style.display = 'none';
                        document.body.offsetHeight;
                        document.body.style.display = '';
                    });
                }, 400);
            } catch (e) {
                console.log('🟡 StatusBar non disponible:', e);
            }

            // 🔥 4. Listener appStateChange
            CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
                if (isActive) {
                    try {
                        await StatusBar.setOverlaysWebView({ overlay: true });
                        requestAnimationFrame(() => {
                            document.body.style.display = 'none';
                            document.body.offsetHeight;
                            document.body.style.display = '';
                        });
                    } catch (e) {
                        console.log('appStateChange error:', e);
                    }
                }
            });

            // 🔥 5. Auth
            await this.ensureUserDisplayId();
            await this.signInAnonymouslyIfNeeded();

            // 🔥 6. Deep link
            const launchUrl = await CapacitorApp.getLaunchUrl();
            if (launchUrl?.url) {
                const url = new URL(launchUrl.url);
                const id = url.pathname.replace('/', '');
                if (id && localStorage.getItem('alreadyConnected') !== 'true') {
                    await this.$router.isReady();
                    this.$router.push({ name: 'book-sms', params: { id } });
                    localStorage.setItem('hasLaunched_v2', 'true');
                    const auth = getAuth();
                    onAuthStateChanged(auth, async (user) => {
                        const userId = user?.uid || localStorage.getItem('userDisplayId');
                        await RevenueCatService.initialize(userId);
                    });
                    window.addEventListener('online', this.handleOnline);
                    window.addEventListener('offline', this.handleOffline);
                    return;
                }
            }

            // 🔥 7. First launch
            await new Promise(resolve => setTimeout(resolve, 300));
            if (!this.getPendingDeepLink()) {
                localStorage.removeItem('hasLaunched');
                await this.checkFirstLaunch();
            }

            // 🔥 8. RevenueCat
            const auth = getAuth();
            onAuthStateChanged(auth, async (user) => {
                const userId = user?.uid || localStorage.getItem('userDisplayId');
                await RevenueCatService.initialize(userId);
            });

            await this.configureNavigationBar() 

        } else {
            // 🔥 1. WEB — langue en premier aussi
            await this.detectAndSaveCountryForWeb();
        }

        window.addEventListener('online', this.handleOnline);
        window.addEventListener('offline', this.handleOffline);
    },

    beforeUnmount() {
        window.removeEventListener('online', this.handleOnline);
        window.removeEventListener('offline', this.handleOffline);
    },
};
</script>

<style>
.global-font {
    font-family: "Plus Jakarta Sans", "Noto Sans", sans-serif;
}

.v-snackbar .net {
    background-color: rgb(0, 0, 0) !important;
    color: white;
    border: 1px solid whitesmoke;
}

.slide-left-enter-active,
.slide-left-leave-active {
    transition: transform 0.3s ease-out, opacity 0.3s;
}

.slide-left-enter-from {
    transform: translateX(-100%);
    opacity: 0;
}

.slide-left-leave-to {
    transform: translateX(100%);
    opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>